import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Send, LifeBuoy } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  API,
  toast,
  useAuthStore,
  useSchool,
} from '@sportschool/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import {
  SendSupportTicketRequestSchema,
  type SendSupportTicketRequest,
} from '@sportschool/shared-types';

import { parseLocalizedText } from '@/lib/localized-settings';

interface SupportContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolId: string;
}

export function SupportContactDialog({
  open,
  onOpenChange,
  schoolId,
}: SupportContactDialogProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const userContext = useAuthStore((s) => s.userContext);

  const { data: school } = useSchool(schoolId);
  const { data: profile } = API.useGetProfile(schoolId, {
    query: {
      enabled: !!schoolId && open,
    },
  });

  const sendSupportTicket = API.useSendSupportTicket();

  // Parse topics from school settings (with active language support) or fallback to default topics
  const topicOptions = useMemo(() => {
    const rawTopicsForLang = parseLocalizedText(school?.supportTopics, i18n.language, 'pl');
    if (rawTopicsForLang && rawTopicsForLang.trim().length > 0) {
      const parsed = rawTopicsForLang
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      if (parsed.length > 0) return parsed;
    }
    return [
      t('support.defaultTopics.payments', 'Płatności i rozliczenia'),
      t('support.defaultTopics.enrollments', 'Zapisy na zajęcia'),
      t('support.defaultTopics.absence', 'Zgłoszenie nieobecności / odrabianie zajęć'),
      t('support.defaultTopics.technical', 'Problem techniczny z aplikacją'),
      t('support.defaultTopics.other', 'Inny temat'),
    ];
  }, [school?.supportTopics, i18n.language, t]);

  const defaultTopic = topicOptions[0] || 'Inny temat';

  const form = useForm<SendSupportTicketRequest>({
    resolver: zodResolver(SendSupportTicketRequestSchema),
    defaultValues: {
      topic: defaultTopic,
      message: '',
      email: userContext?.user?.email || profile?.email || '',
      phoneNumber: profile?.phoneNumber || '',
    },
  });

  // Keep email and phone updated when profile/userContext loads
  useEffect(() => {
    if (open) {
      const emailVal = profile?.email || userContext?.user?.email || '';
      const phoneVal = profile?.phoneNumber || '';
      form.setValue('email', emailVal);
      form.setValue('phoneNumber', phoneVal);
      if (!form.getValues('topic')) {
        form.setValue('topic', defaultTopic);
      }
    }
  }, [open, profile, userContext, defaultTopic, form]);

  const onSubmit = async (data: SendSupportTicketRequest) => {
    try {
      await sendSupportTicket.mutateAsync({
        schoolId,
        data: {
          topic: data.topic,
          message: data.message,
          email: data.email,
          phoneNumber: data.phoneNumber ? data.phoneNumber.trim() : null,
        },
      });

      toast.success(t('support.sendSuccess', 'Twoja wiadomość została wysłana do obsługi szkoły'));
      form.reset({
        topic: defaultTopic,
        message: '',
        email: userContext?.user?.email || profile?.email || '',
        phoneNumber: data.phoneNumber || profile?.phoneNumber || '',
      });
      // Invalidate profile query in case user's phone number was saved
      queryClient.invalidateQueries({ queryKey: API.getGetProfileQueryKey(schoolId) });
      onOpenChange(false);
    } catch {
      toast.error(t('support.sendError', 'Wystąpił błąd podczas wysyłania wiadomości. Spróbuj ponownie.'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeButtonVariant="onDark" className="max-h-[90vh] p-0 overflow-hidden flex flex-col gap-0 border border-border bg-card rounded-2xl shadow-xl sm:max-w-lg">
        {/* Header */}
        <DialogHeader className="relative bg-gradient-to-br from-[#003554] to-[#017EBA] p-5 sm:p-6 text-white shrink-0 overflow-hidden text-left space-y-1">
          <div className="flex items-center gap-3 relative z-10">
            <div className="size-10 rounded-xl bg-white/15 flex items-center justify-center text-white shrink-0 border border-white/20">
              <LifeBuoy className="size-5" />
            </div>
            <div>
              <DialogTitle className="font-extrabold text-white text-lg tracking-tight">
                {t('support.dialogTitle', 'Pomoc i kontakt z obsługą')}
              </DialogTitle>
              <DialogDescription className="text-xs text-sky-100/90 mt-0.5">
                {t('support.dialogDescription', 'Wybierz temat, opisz problem lub pytanie, a my odpowiemy najszybciej jak to możliwe.')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Body */}
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-y-auto p-5 sm:p-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="support-topic" className="text-sm font-semibold">
              {t('support.topicLabel', 'Temat zgłoszenia')} <span className="text-destructive">*</span>
            </Label>
            <Controller
              control={form.control}
              name="topic"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="support-topic" className="w-full">
                    <SelectValue placeholder={t('support.topicPlaceholder', 'Wybierz temat')} />
                  </SelectTrigger>
                  <SelectContent>
                    {topicOptions.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {form.formState.errors.topic && (
              <p className="text-xs text-destructive">{form.formState.errors.topic.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="support-message" className="text-sm font-semibold">
              {t('support.messageLabel', 'Wiadomość / opis problemu')} <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="support-message"
              rows={5}
              placeholder={t('support.messagePlaceholder', 'Opisz dokładnie sprawę lub problem...')}
              className={cn(form.formState.errors.message && 'border-destructive')}
              {...form.register('message')}
            />
            {form.formState.errors.message && (
              <p className="text-xs text-destructive">{form.formState.errors.message.message}</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="support-email" className="text-sm font-semibold">
                {t('support.emailLabel', 'Twój adres e-mail')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="support-email"
                type="email"
                placeholder={t('support.emailPlaceholder', 'twoj@email.com')}
                className={cn(form.formState.errors.email && 'border-destructive')}
                {...form.register('email')}
              />
              {form.formState.errors.email && (
                <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="support-phone" className="text-sm font-semibold">
                {t('support.phoneLabel', 'Numer telefonu')}
              </Label>
              <Input
                id="support-phone"
                type="tel"
                placeholder={t('support.phonePlaceholder', '+48 123 456 789')}
                className={cn(form.formState.errors.phoneNumber && 'border-destructive')}
                {...form.register('phoneNumber')}
              />
              {form.formState.errors.phoneNumber && (
                <p className="text-xs text-destructive">{form.formState.errors.phoneNumber.message}</p>
              )}
            </div>
          </div>

          <DialogFooter className="pt-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={sendSupportTicket.isPending}
            >
              {t('common.cancel', 'Anuluj')}
            </Button>
            <Button
              type="submit"
              disabled={sendSupportTicket.isPending}
              className="gap-2"
            >
              <Send className="size-4" />
              {sendSupportTicket.isPending
                ? t('support.sending', 'Wysyłanie...')
                : t('support.sendButton', 'Wyślij wiadomość')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
